import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TrendingUp, Users, Share2, PartyPopper, Flag } from "lucide-react";
import { toast } from "sonner";
import { getDonationStats } from "../lib/api";

const fmt = (n) => "$" + Math.round(n).toLocaleString();
const MILESTONES = [50, 75, 100];

export const DonationProgress = ({ onGive }) => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    getDonationStats().then(setStats).catch(() => setStats(null));
  }, []);

  const share = async () => {
    const url = window.location.origin + "/donate";
    const reached = stats ? MILESTONES.filter((m) => stats.percent >= m).pop() : null;
    const text = reached
      ? `Iron Shield's Reset Housing Campaign just hit ${reached}% — help us keep going for veterans in crisis.`
      : `Help Iron Shield reach its Reset Housing goal for veterans in crisis — ${stats ? stats.percent : 0}% of the way there.`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Iron Shield Reset Housing Campaign", text, url });
      } else {
        await navigator.clipboard.writeText(`${text} ${url}`);
        toast.success("Campaign link copied — thanks for sharing!");
      }
    } catch (e) { /* user cancelled share */ }
  };

  if (!stats) return null;
  const pct = Math.max(2, Math.min(100, stats.percent));
  const reached = MILESTONES.filter((m) => stats.percent >= m).pop() || null;
  const next = MILESTONES.find((m) => stats.percent < m) || null;

  return (
    <section className="py-16 md:py-20 bg-white border-y border-softgray" data-testid="donation-progress">
      <div className="container-x max-w-4xl">
        <div className="i-card p-8 md:p-10">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="label-caps text-navy flex items-center gap-2"><TrendingUp className="h-4 w-4 text-gold" /> Reset Housing Campaign</div>
              <div className="mt-3 flex items-baseline gap-3">
                <span className="font-heading text-4xl md:text-5xl font-black text-navy tracking-tight" data-testid="progress-raised">{fmt(stats.raised)}</span>
                <span className="text-charcoal/60 text-base md:text-lg">raised of {fmt(stats.goal)} goal</span>
              </div>
            </div>
            <div className="text-right">
              <div className="font-heading text-3xl md:text-4xl font-black text-gold-deep">{stats.percent}%</div>
              <div className="mt-1 flex items-center gap-1.5 text-sm text-slate"><Users className="h-4 w-4" /> {stats.donor_count} supporters</div>
            </div>
          </div>

          {/* Progress bar with milestone markers */}
          <div className="mt-7 relative">
            <div className="h-4 w-full rounded-full bg-softgray overflow-hidden" role="progressbar" aria-valuenow={stats.percent} aria-valuemin={0} aria-valuemax={100}>
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-navy to-gold"
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.25 }}
                data-testid="progress-bar-fill"
              />
            </div>
            {MILESTONES.map((m) => {
              const hit = stats.percent >= m;
              return (
                <div key={m} className="absolute -top-1.5" style={{ left: `${m}%` }} data-testid={`milestone-marker-${m}`}>
                  <div className={`h-7 w-0.5 -translate-x-1/2 ${hit ? "bg-gold" : "bg-slate/40"}`} />
                  <div className={`mt-1 -translate-x-1/2 flex items-center gap-1 text-[10px] font-bold ${hit ? "text-gold-deep" : "text-slate"}`}>
                    {hit && <Flag className="h-3 w-3" />} {m}%
                  </div>
                </div>
              );
            })}
          </div>

          {/* Milestone celebration / next-goal nudge */}
          <AnimatePresence mode="wait">
            {reached ? (
              <motion.div
                key={`reached-${reached}`}
                initial={{ opacity: 0, y: 10, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="mt-10 rounded-xl border border-gold/40 bg-gold/10 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
                data-testid="milestone-celebration"
              >
                <div className="flex items-start gap-3">
                  <PartyPopper className="h-6 w-6 text-gold-deep shrink-0" />
                  <div>
                    <div className="font-heading text-lg font-bold text-navy">We hit {reached}% of our goal! 🎉</div>
                    <p className="text-sm text-charcoal/75">{reached === 100 ? "Goal reached — thank you for standing with veterans. Every extra gift extends our reach." : `Thank you — help us push toward ${next}% and keep more beds open.`}</p>
                  </div>
                </div>
                <button onClick={share} data-testid="milestone-share-btn" className="shrink-0 inline-flex items-center gap-2 bg-navy text-offwhite px-5 py-3 rounded-lg text-sm font-bold hover:bg-navy-deep transition-colors">
                  <Share2 className="h-4 w-4 text-gold" /> Share this moment
                </button>
              </motion.div>
            ) : (
              <motion.div key="nudge" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <p className="text-sm text-charcoal/70">
                  {next ? <>Just <span className="font-bold text-navy">{next - stats.percent}%</span> to our next milestone (<span className="font-semibold text-gold-deep">{next}%</span>). Every gift moves a veteran closer to a safe reset.</> : "Every gift moves a veteran closer to a safe reset."}
                </p>
                <div className="flex gap-3">
                  <button onClick={share} data-testid="progress-share-btn" className="shrink-0 inline-flex items-center gap-2 border border-navy text-navy px-5 py-3 rounded-lg text-sm font-semibold hover:bg-navy hover:text-offwhite transition-colors">
                    <Share2 className="h-4 w-4" /> Share
                  </button>
                  <button onClick={onGive} data-testid="progress-give-btn" className="shrink-0 inline-flex items-center gap-2 bg-gold text-charcoal px-6 py-3 rounded-lg text-sm font-bold uppercase tracking-wide hover:bg-gold-deep transition-colors">
                    Add your gift
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default DonationProgress;
